import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { $trycatch } from '@tszen/trycatch';
import clsx from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import Link from 'next/link';
import { Fragment, useTransition } from 'react';
import { toast } from 'sonner';
import { colorModeSettingAtom } from '@/atoms';
import { accountAtom, avatarSourceAtom } from '@/atoms/auth';
import { COLOR_MODE } from '@/components/themes';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Menubar, MenubarContent, MenubarMenu, MenubarRadioGroup, MenubarRadioItem, MenubarTrigger } from '@/components/ui/menubar';
import { Slot } from '@/components/ui/slot';
import { createClient } from '@/lib/supabase/client';
import { cn } from '@/lib/utils';
import { useChangeLocale, useCurrentLocale, useI18n } from '@/locales/client';
import { bindApply, omitUndefined } from '@sub-vocab/utils/lib';
const LOCALES = [{
  value: 'en',
  label: 'English'
}, {
  value: 'zh',
  label: '简体中文'
}] as const;
function Account(t0) {
  const $ = _c(12);
  let className;
  let props;
  if ($[0] !== t0) {
    const {
      className: t1,
      style,
      ...t2
    } = t0;
    className = t1;
    props = t2;
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("inline-flex items-center gap-3", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props) {
    t2 = omitUndefined(props);
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <svg className="icon-[lucide--cog] size-4" />;
    t4 = <span>Account</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2) {
    t5 = <Link href="/register" className={t1} {...t2}>{t3}{t4}</Link>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function SignIn(t0) {
  const $ = _c(12);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("inline-flex items-center gap-3", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props) {
    t2 = omitUndefined(props);
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <svg className="icon-[mingcute--user-4-fill] size-4" />;
    t4 = <span>Sign in</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2) {
    t5 = <Link href="/login" className={t1} {...t2}>{t3}{t4}</Link>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function SignOut(t0) {
  const $ = _c(19);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const t = useI18n();
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    const supabase = createClient();
    t1 = useMutation;
    t2 = {
      mutationKey: ["signOut"],
      mutationFn: bindApply(supabase.auth.signOut, supabase.auth)
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const {
    mutateAsync: signOut
  } = t1(t2);
  let t3;
  if ($[5] !== signOut) {
    t3 = async () => {
      const [value, error] = await $trycatch(signOut([{
        scope: "local"
      }]));
      if (error !== null) {
        toast.error(error.message);
      } else {
        const {
          error: error_0
        } = value;
        if (error_0) {
          toast.error(error_0.message);
        }
      }
    };
    $[5] = signOut;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const logout = t3;
  let t4;
  if ($[7] !== className) {
    t4 = cn("inline-flex items-center gap-3", className);
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <svg className="icon-[solar--logout-2-outline] size-4 -scale-x-100" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("SignOut");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <span>{t6}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== logout || $[15] !== props || $[16] !== t4 || $[17] !== t7) {
    t8 = <button type="button" aria-label="logout" className={t4} {...props} onClick={logout}>{t5}{t7}</button>;
    $[14] = logout;
    $[15] = props;
    $[16] = t4;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
export function TopBar(t0) {
  const $ = _c(40);
  const {
    className
  } = t0;
  useI18n();
  const changeLocale = useChangeLocale();
  const locale = useCurrentLocale();
  const account = useAtomValue(accountAtom);
  const [colorModeSetting, setColorThemeSetting] = useAtom(colorModeSettingAtom);
  const [isThemeTransitioning, startThemeTransition] = useTransition();
  const avatarSource = useAtomValue(avatarSourceAtom);
  let t1;
  if ($[0] !== className) {
    t1 = cn(className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = clsx("group/nav z-20 w-full rounded-t-3xl bg-background");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== colorModeSetting) {
    t3 = COLOR_MODE.ALL.find(theme => theme.value === colorModeSetting)?.icon ?? COLOR_MODE.DEFAULT.icon;
    $[3] = colorModeSetting;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <MenubarTrigger aria-label="theme" className="size-8 justify-center p-0"><div className="flex h-full items-center"><Slot className="size-4.25 text-neutral-500 dark:text-neutral-400">{t3}</Slot></div></MenubarTrigger>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = isThemeTransitioning && "[body:has(&)_*::after]:transition-none! [body:has(&)_*::before]:transition-none! [body:has(&)_*:not(main)]:transition-none!";
  let t6;
  if ($[7] !== t5) {
    t6 = clsx(t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== colorModeSetting || $[10] !== setColorThemeSetting) {
    t7 = COLOR_MODE.ALL.map(theme_0 => <MenubarRadioItem key={theme_0.value} value={theme_0.value} disabled={theme_0.value === colorModeSetting} onSelect={() => {
      setColorThemeSetting(theme_0.value);
      startThemeTransition(_temp);
    }}>{theme_0.label}</MenubarRadioItem>);
    $[9] = colorModeSetting;
    $[10] = setColorThemeSetting;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== colorModeSetting || $[13] !== t6 || $[14] !== t7) {
    t8 = <MenubarContent className="min-w-0" align="end" sideOffset={3}><MenubarRadioGroup value={colorModeSetting} className={t6}>{t7}</MenubarRadioGroup></MenubarContent>;
    $[12] = colorModeSetting;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t8) {
    t9 = <Menubar className="h-full border-0 bg-transparent p-0 shadow-none"><MenubarMenu>{t4}{t8}</MenubarMenu></Menubar>;
    $[16] = t4;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <MenubarTrigger aria-label="language" className="size-8 justify-center p-0"><div className="flex h-full items-center"><svg className="icon-[fa--language] size-4 text-neutral-500 dark:text-neutral-400" /></div></MenubarTrigger>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== changeLocale || $[21] !== locale) {
    t11 = LOCALES.map(language => <MenubarRadioItem key={language.value} value={language.value} disabled={language.value === locale} onSelect={() => {
      changeLocale(language.value);
    }}>{language.label}</MenubarRadioItem>);
    $[20] = changeLocale;
    $[21] = locale;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== locale || $[24] !== t11) {
    t12 = <Menubar className="h-full border-0 bg-transparent p-0 shadow-none"><MenubarMenu>{t10}<MenubarContent className="min-w-0" align="end" sideOffset={3}><MenubarRadioGroup value={locale}>{t11}</MenubarRadioGroup></MenubarContent></MenubarMenu></Menubar>;
    $[23] = locale;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== avatarSource) {
    t13 = <DropdownMenuTrigger aria-label="user" className="flex size-8 p-0 select-none [--sq-r:.75rem]" suppressHydrationWarning={true} render={avatarSource ? <Button variant="ghost"><img src={avatarSource} alt="avatar" className="rounded-full border" /></Button> : <Button variant="ghost"><svg className="icon-[mingcute--user-4-fill] size-5.5 text-neutral-500" /></Button>} />;
    $[26] = avatarSource;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== account) {
    t14 = <DropdownMenuContent className="w-[unset] [&_[role=menuitem]_svg]:text-neutral-600 [&_[role=menuitem]>*]:grow [&_[role=menuitem]>*]:px-2 [&_[role=menuitem]>*]:py-1.5 **:[[role=menuitem]]:p-0" align="end">{account ? <Fragment><DropdownMenuLabel>{account}</DropdownMenuLabel><DropdownMenuSeparator /><DropdownMenuGroup><DropdownMenuItem><SignOut /></DropdownMenuItem></DropdownMenuGroup></Fragment> : <Fragment><DropdownMenuGroup><DropdownMenuItem><Account /></DropdownMenuItem></DropdownMenuGroup><DropdownMenuSeparator /><DropdownMenuGroup><DropdownMenuItem><SignIn /></DropdownMenuItem></DropdownMenuGroup></Fragment>}</DropdownMenuContent>;
    $[28] = account;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <div className="flex size-8 items-center justify-center"><DropdownMenu>{t13}{t14}</DropdownMenu></div>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t12 || $[34] !== t15 || $[35] !== t9) {
    t16 = <nav className={t2}><Fragment><Fragment><div><div><div className="flex h-11 items-center justify-between"><div className="flex h-full items-center gap-2">{t9}{t12}{t15}</div></div></div></div></Fragment></Fragment></nav>;
    $[33] = t12;
    $[34] = t15;
    $[35] = t9;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t1 || $[38] !== t16) {
    t17 = <div className={t1}>{t16}</div>;
    $[37] = t1;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}
function _temp() {}